import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavItem } from '@im/ui-lib/nav';
import {
  useDecodedToken,
  useHasPermission,
  useIsFeatureWhitelisted,
} from 'mf_core/auth';
import { framedRoute } from './nav/utils';
import { useBuildVars, useSiteConfig } from 'mf_core/config';
import {
  Configurator,
  useConfiguratorMenuQuery,
} from '../hooks/use-configuratormenu';
import { useFeatureFlagBase } from '../hooks/use-feature-flag-base';

export function useAvatarProps() {
  const navigate = useNavigate();
  const { siteCode } = useSiteConfig();
  const decodedToken = useDecodedToken();
  const { buildEnvironment } = useBuildVars();
  const isFeatureWhitelisted = useIsFeatureWhitelisted();
  const isCmpWhitelisted = isFeatureWhitelisted('CMP');
  const { data: configuratorResponse } = useConfiguratorMenuQuery();
  const [configuratorMenus, setConfiguratorMenus] = useState<
    NavItem[] | undefined
  >(undefined);
  const [isNavigationRequired, setIsNavigationRequired] = useState();
  const hasPermission = useHasPermission();
  const { boValues, isReady } = useFeatureFlagBase();

  // used to enable legacy IMO Report Preferences
  const isReportPreferencesEnabled = useMemo(() => {
    const whitelist = boValues['Reseller_Whitelist_For_Order_Status_Tracking'];
    const resellerId = decodedToken?.resellerId;
    return (
      hasPermission(['ResellerAdmin']) &&
      isNavigationRequired === 'true' &&
      Boolean(isReady) &&
      boValues['ACC_MGMT_EPP_WhitelistedResellers'] !== 'true' &&
      boValues['Enable_Order_Status_Report'] === 'true' &&
      Boolean(
        whitelist?.trim() === '*' || whitelist?.split(',')?.includes(resellerId)
      )
    );
  }, [
    boValues,
    decodedToken?.resellerId,
    hasPermission,
    isNavigationRequired,
    isReady,
  ]);

  useEffect(() => {
    if (configuratorResponse && configuratorResponse.externalHost) {
      const menuItems: NavItem[] = [];
      configuratorResponse.externalHost.forEach((item: Configurator) => {
        if (item.openUrlInNewTab) {
          menuItems.push({
            key: item.name,
            label: item.name,
            url: item.url,
          });
        } else {
          menuItems.push({
            key: item.name,
            label: item.name,
            route: `/my/productconfigurator/cms?page=${item.nodeAliasPath}`,
          });
        }
      });
      setConfiguratorMenus(menuItems);
    }
  }, [configuratorResponse]);
  useEffect(() => {
    setIsNavigationRequired(boValues['ACC_MGMT_EnableX4CReactUI']);
  }, [boValues]);
  return useMemo(() => {
    return {
      firstName: decodedToken?.firstName || '',
      lastName: decodedToken?.lastName || '',
      navItems: [
        {
          key: 'myprofile.AccountSettings',
          route:
            isNavigationRequired === 'false' ||
            isNavigationRequired === undefined
              ? framedRoute('/Site/MyProfile')
              : '/my/account/accountsettings',
        },
        isReportPreferencesEnabled
          ? {
              key: 'myprofile.ReportPreferences',
              route: framedRoute('/Site/MyProfile/ReportPreferences'),
            }
          : null,
        isNavigationRequired === 'true' &&
        isReady &&
        boValues['ACC_MGMT_Enable_BrandingPreferences'] === 'true' &&
        hasPermission(['CreateQuotes'])
          ? {
              key: 'myprofile.BrandingPreferences',
              route: framedRoute('/Site/MyProfile/BrandingPreferences'),
            }
          : null,
        !isCmpWhitelisted
          ? {
              key: 'myprofile.CustomizeHomepage',
              route: '/my/dashboardcustomization',
            }
          : null,
        {
          key: 'myprofile.SavedCarts',
          route: '/my/savedcarts',
        },
        siteCode === 'fr'
          ? {
              key: 'myprofile.CarteBlue',
              route: '/my/payment/payline',
            }
          : null,
        configuratorMenus && configuratorMenus.length > 0
          ? {
              key: 'myprofile.Configurators',
              navItems: configuratorMenus,
            }
          : null,
        buildEnvironment.startsWith('staging') && {
          key: 'Developer tools',
          route: '/developer-tools',
        },
        // remove null entries from array
      ].filter(Boolean) as unknown as NavItem[],
      onLogoutClick: () => navigate('/logout'),
      resellerID: decodedToken?.resellerId,
      username: decodedToken?.imoProfile?.displayUserName,
    };
  }, [
    buildEnvironment,
    configuratorMenus,
    hasPermission,
    navigate,
    decodedToken,
    siteCode,
    isCmpWhitelisted,
    isNavigationRequired,
    boValues,
    isReady,
    isReportPreferencesEnabled,
  ]);
}
